import React, { useState, useEffect } from 'react';
import { 
  Sliders, 
  Play, 
  RotateCcw, 
  Save, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Clock,
  Percent
} from 'lucide-react';
import type { SimulationParameters, FinancialKPIs } from '../../types/businessPlan';

interface SimulationControlsProps {
  onSimulate: (parameters: SimulationParameters) => Promise<void>;
  onSaveScenario: (name: string, parameters: SimulationParameters) => Promise<void>;
  isSimulating?: boolean;
  currentKPIs?: FinancialKPIs;
  className?: string;
}

export const SimulationControls: React.FC<SimulationControlsProps> = ({
  onSimulate,
  onSaveScenario,
  isSimulating = false,
  currentKPIs,
  className = ''
}) => {
  const [parameters, setParameters] = useState<SimulationParameters>({
    ca_variation: 0,
    charges_fixes_variation: 0,
    charges_variables_variation: 0,
    payment_delay_days: 0,
    interest_rate_variation: 0,
    custom_adjustments: {}
  });

  const [scenarioName, setScenarioName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const hasAnyChange = Object.values(parameters).some(value => 
      typeof value === 'number' ? value !== 0 : Object.keys(value).length > 0
    );
    setHasChanges(hasAnyChange);
  }, [parameters]);

  const handleParameterChange = (key: keyof SimulationParameters, value: number) => {
    setParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetParameters = () => {
    setParameters({
      ca_variation: 0,
      charges_fixes_variation: 0,
      charges_variables_variation: 0,
      payment_delay_days: 0,
      interest_rate_variation: 0,
      custom_adjustments: {}
    });
  };

  const handleSimulate = async () => {
    await onSimulate(parameters);
  };

  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) return;
    
    await onSaveScenario(scenarioName.trim(), parameters);
    setShowSaveModal(false);
    setScenarioName('');
  };

  const getVariationColor = (value: number) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getVariationIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="w-4 h-4" />;
    if (value < 0) return <TrendingDown className="w-4 h-4" />;
    return <Percent className="w-4 h-4" />;
  };

  const presetScenarios = [
    { name: 'Optimiste', ca: 20, charges_fixes: -5, charges_variables: -2 },
    { name: 'Pessimiste', ca: -20, charges_fixes: 10, charges_variables: 5 },
    { name: 'Crise', ca: -40, charges_fixes: 15, charges_variables: 10 },
    { name: 'Croissance', ca: 50, charges_fixes: 20, charges_variables: 0 }
  ];

  const applyPreset = (preset: typeof presetScenarios[0]) => {
    setParameters(prev => ({
      ...prev,
      ca_variation: preset.ca,
      charges_fixes_variation: preset.charges_fixes,
      charges_variables_variation: preset.charges_variables
    }));
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Sliders className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Simulation de Scénarios</h2>
            <p className="text-sm text-gray-600">Testez l'impact de différentes hypothèses</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={resetParameters}
            disabled={!hasChanges}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset</span>
          </button>
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={!hasChanges}
            className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>Sauvegarder</span>
          </button>
        </div>
      </div>

      {/* Preset Scenarios */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Scénarios Prédéfinis</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {presetScenarios.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900 text-sm">{preset.name}</p>
              <p className="text-xs text-gray-600">
                CA {preset.ca > 0 ? '+' : ''}{preset.ca}%
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Simulation Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* CA Variation */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Chiffre d'Affaires
            </label>
            <div className={`flex items-center space-x-1 ${getVariationColor(parameters.ca_variation)}`}>
              {getVariationIcon(parameters.ca_variation)}
              <span className="font-bold">
                {parameters.ca_variation > 0 ? '+' : ''}{parameters.ca_variation}%
              </span>
            </div>
          </div>
          <input
            type="range"
            min="-50"
            max="50"
            step="5"
            value={parameters.ca_variation}
            onChange={(e) => handleParameterChange('ca_variation', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>-50%</span>
            <span>0%</span>
            <span>+50%</span>
          </div>
        </div>

        {/* Charges Fixes */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              🏢 Charges Fixes
            </label>
            <div className={`flex items-center space-x-1 ${getVariationColor(parameters.charges_fixes_variation)}`}>
              {getVariationIcon(parameters.charges_fixes_variation)}
              <span className="font-bold">
                {parameters.charges_fixes_variation > 0 ? '+' : ''}{parameters.charges_fixes_variation}%
              </span>
            </div>
          </div>
          <input
            type="range"
            min="-30"
            max="30"
            step="2"
            value={parameters.charges_fixes_variation}
            onChange={(e) => handleParameterChange('charges_fixes_variation', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>-30%</span>
            <span>0%</span>
            <span>+30%</span>
          </div>
        </div>

        {/* Charges Variables */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              📈 Charges Variables
            </label>
            <div className={`flex items-center space-x-1 ${getVariationColor(parameters.charges_variables_variation)}`}>
              {getVariationIcon(parameters.charges_variables_variation)}
              <span className="font-bold">
                {parameters.charges_variables_variation > 0 ? '+' : ''}{parameters.charges_variables_variation}%
              </span>
            </div>
          </div>
          <input
            type="range"
            min="-20"
            max="20"
            step="1"
            value={parameters.charges_variables_variation}
            onChange={(e) => handleParameterChange('charges_variables_variation', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>-20%</span>
            <span>0%</span>
            <span>+20%</span>
          </div>
        </div>

        {/* Payment Delay */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              <Clock className="w-4 h-4 inline mr-1" />
              Retard de Paiement
            </label>
            <span className={`font-bold ${parameters.payment_delay_days > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {parameters.payment_delay_days} jours
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="90"
            step="5"
            value={parameters.payment_delay_days}
            onChange={(e) => handleParameterChange('payment_delay_days', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>0j</span>
            <span>45j</span>
            <span>90j</span>
          </div>
        </div>

        {/* Interest Rate */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              💳 Taux d'Intérêt
            </label>
            <div className={`flex items-center space-x-1 ${getVariationColor(parameters.interest_rate_variation)}`}>
              {getVariationIcon(parameters.interest_rate_variation)}
              <span className="font-bold">
                {parameters.interest_rate_variation > 0 ? '+' : ''}{parameters.interest_rate_variation}%
              </span>
            </div>
          </div>
          <input
            type="range"
            min="-5"
            max="10"
            step="0.5"
            value={parameters.interest_rate_variation}
            onChange={(e) => handleParameterChange('interest_rate_variation', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>-5%</span>
            <span>0%</span>
            <span>+10%</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          {hasChanges ? (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span>Paramètres modifiés - Cliquez "Simuler" pour voir l'impact</span>
            </div>
          ) : (
            <span>Ajustez les paramètres ci-dessus pour simuler différents scénarios</span>
          )}
        </div>

        <button
          onClick={handleSimulate}
          disabled={!hasChanges || isSimulating}
          className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 shadow-lg hover:shadow-xl"
        >
          {isSimulating ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Simulation...</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              <span>Simuler l'Impact</span>
            </>
          )}
        </button>
      </div>

      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Sauvegarder le Scénario</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="scenario-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du scénario
                  </label>
                  <input
                    id="scenario-name"
                    type="text"
                    value={scenarioName}
                    onChange={(e) => setScenarioName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Scénario optimiste Q1 2024"
                  />
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Paramètres du scénario :</h4>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div>CA : {parameters.ca_variation > 0 ? '+' : ''}{parameters.ca_variation}%</div>
                    <div>Charges fixes : {parameters.charges_fixes_variation > 0 ? '+' : ''}{parameters.charges_fixes_variation}%</div>
                    <div>Charges variables : {parameters.charges_variables_variation > 0 ? '+' : ''}{parameters.charges_variables_variation}%</div>
                    {parameters.payment_delay_days > 0 && (
                      <div>Retard paiement : {parameters.payment_delay_days} jours</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveScenario}
                  disabled={!scenarioName.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};